import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform } from "framer-motion";
import api from "../lib/api";
import { Reveal, MaskLine } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { Footer } from "../components/Footer";

const HERO = "https://images.unsplash.com/photo-1595024600400-2a49b9fce270?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHx3aGVhdCUyMGZpZWxkfGVufDB8fHx8MTc4NjQ4MzQ4NHww&ixlib=rb-4.1.0&q=85";

const chapters = [
  { n: "01", t: "The land", d: "Every frame is made within a day's drive of home — the wide-open corner of Northeast Montana where the wheat runs clean to the horizon." },
  { n: "02", t: "The craft", d: "Printed to order on premium archival photographic paper, rated to hold its color for over 100 years on your wall." },
  { n: "03", t: "The lab", d: "Each print is produced at a professional photo lab in the USA, then shipped to your door — always fast, always free." },
];

export default function Home() {
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 160]);
  const heroScale = useTransform(scrollY, [0, 700], [1, 1.15]);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      const id = location.state.scrollTo;
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, [location]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/prints", { params: { featured: true } }).then((r) => setFeatured(r.data));
  }, []);

  return (
    <div>
      <Helmet>
        <title>Prairie Prints MT | Fine Art Photography from Northeast Montana</title>
        <meta name="description" content="Archival fine art photography prints from rural Northeast Montana by Mary — landscapes, weathered barns, farm equipment, vintage trucks, wildlife and wheat fields. Printed to last 100+ years, free shipping." />
      </Helmet>
      {/* HERO */}
      <section className="relative h-screen overflow-hidden flex items-end">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO} alt="Montana wheat field" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-ink/40" />
        </motion.div>
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24 text-paper">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="label text-[#F2D486] block mb-6 [text-shadow:_0_1px_18px_rgba(0,0,0,0.95)]">Prairie Prints MT — Scobey, Montana</motion.span>
          <h1 className="font-serif text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem] leading-[0.9] tracking-tight">
            <MaskLine delay={0.3}>Fine art from</MaskLine>
            <MaskLine delay={0.45}><span className="italic text-[#F2D486]">the wide open.</span></MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-md text-paper/80 text-base leading-relaxed">
            Landscapes, weathered barns, rusted trucks and rugged wildlife — photographed across rural Northeast Montana and printed to last a lifetime.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="mt-10 flex gap-4 flex-wrap">
            <Link to="/gallery/landscapes" data-testid="hero-shop-btn"
              className="label bg-paper text-ink px-8 py-4 hover:bg-wheat transition-colors duration-300">Shop prints</Link>
            <a href="#story" data-testid="hero-story-btn"
              className="label border border-paper/40 px-8 py-4 hover:bg-paper/10 transition-colors duration-300">Meet the photographer</a>
          </motion.div>
        </div>
      </section>

      <EditorialMarquee />

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <div>
            <span className="label text-brick">Selected works</span>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight mt-3">Featured prints</h2>
          </div>
          <Link to="/gallery/landscapes" data-testid="featured-viewall" className="label text-ink border-b border-ink pb-1 hover:text-brick hover:border-brick transition-colors">View everything</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          {featured.slice(0, 6).map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.1}>
              <Link to={`/print/${p.id}`} data-testid={`featured-print-${p.id}`} className="group block">
                <div className={`overflow-hidden border border-line bg-paper-2 ${i % 3 === 1 ? "aspect-[4/5]" : "aspect-[4/3]"}`}>
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out" />
                </div>
                <div className="flex items-baseline justify-between mt-4">
                  <h3 className="font-serif text-2xl tracking-tight">{p.title}</h3>
                  <span className="label text-stone">${p.sizes[0].price.toFixed(0)}+</span>
                </div>
                <span className="label text-stone">{p.category_name}</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-paper-2 border-y border-line py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <span className="label text-brick">Browse by collection</span>
          <h2 className="font-serif text-5xl md:text-6xl tracking-tight mt-3 mb-16">Six ways to wander the prairie</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cats.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 0.08}>
                <Link to={`/gallery/${c.slug}`} data-testid={`category-${c.slug}`} className="group block relative overflow-hidden border border-line aspect-[3/2]">
                  <img src={c.cover} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6 text-paper">
                    <h3 className="font-serif text-3xl tracking-tight">{c.name}</h3>
                    <span className="label text-paper/70">{c.count} prints →</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        {chapters.map((c, i) => (
          <Reveal key={c.n}>
            <div className={`grid md:grid-cols-12 gap-6 md:gap-12 py-12 ${i !== 0 ? "border-t border-line" : ""}`}>
              <div className="md:col-span-3 font-serif text-6xl md:text-7xl text-wheat">{c.n}.</div>
              <h3 className="md:col-span-4 font-serif text-4xl md:text-5xl tracking-tight">{c.t}</h3>
              <p className="md:col-span-5 text-stone text-lg leading-relaxed self-center">{c.d}</p>
            </div>
          </Reveal>
        ))}
      </section>

      {/* ABOUT / STORY */}
      <section id="story" className="bg-ink text-paper py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="overflow-hidden border border-line-dark aspect-[4/5]">
              <img src="https://images.unsplash.com/photo-1488724485310-bd445f3faff6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxwaG90b2dyYXBoZXIlMjBvdXRkb29yfGVufDB8fHx8MTc4NjQ4MzQ4NXww&ixlib=rb-4.1.0&q=85" alt="Mary, photographer" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="label text-wheat" id="about">The photographer</span>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight mt-4 mb-8">Just a girl taking photos of the great outdoors.</h2>
            <p className="text-paper/80 text-lg leading-relaxed">
              Hi, I'm Mary — the heart behind Prairie Prints MT. From my corner of rural Northeast Montana, I photograph the wide-open beauty of this land: its sweeping landscapes, rugged wildlife, timeworn farm equipment, vintage trucks, and the weathered buildings that hold stories of the past.
            </p>
            <p className="text-paper/60 mt-6 leading-relaxed">
              Each print is made to order on premium archival paper rated to last over 100 years, produced at a professional photo lab in the USA — with fast, free shipping.
            </p>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
